/** @format */

import React from "react";
import { Box, Typography, Chip, Stack, CircularProgress } from "@mui/material";
import {
    CheckCircleOutline,
    CancelOutlined,
    InfoOutlined,
    TrendingFlat,
} from "@mui/icons-material";

import {
    CollectSectionCard,
    CollectSectionHeader,
    CollectSectionTitle,
    CollectSectionSubtitle,
    CollectKpiCard,
    CollectKpiLabel,
    CollectKpiValue,
    CollectKpiHint,
    CollectPrimaryButton,
    CollectInfoBanner,
} from "../styles";
import { colors } from "../../../utility/types/constant";
import {
    IPredictionAnalysis,
    IPredictionReasonEntry,
    PredictionRiskLevel,
    PREDICTION_RISK_LEVEL_LABELS,
    PREDICTION_CONFIDENCE_LABELS,
    RECOVERY_ACTION_LABELS,
} from "../../../utility/types/collectTypes";

// ─────────────────────────────────────────────────────────────────────────
// PredictionSummary (Stage 2J) — headline forecast: reliability score,
// outcome probabilities, confidence, risk level, the plain-English reasons,
// and the recommended recovery action. Always renders the compliance
// disclaimer. "Apply" is gated to owners/admins by the parent page.
// ─────────────────────────────────────────────────────────────────────────

interface Props {
    prediction: IPredictionAnalysis;
    canMutate: boolean;
    applying: boolean;
    onApply: () => void;
}

const FONT = "'Montserrat', sans-serif";

const RISK_COLORS: Record<PredictionRiskLevel, string> = {
    low: "#16a34a",
    medium: "#f59e0b",
    high: "#ea580c",
    critical: "#dc2626",
};

function pct(value: number): string {
    return `${Math.round((value ?? 0) * 100)}%`;
}

function fmtDate(value?: string): string {
    if (!value) return "—";
    try {
        return new Date(value).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
        });
    } catch {
        return "—";
    }
}

const REASON_ICON: Record<IPredictionReasonEntry["direction"], React.ReactNode> = {
    positive: <CheckCircleOutline fontSize="small" sx={{ color: "#16a34a" }} />,
    negative: <CancelOutlined fontSize="small" sx={{ color: "#dc2626" }} />,
    neutral: <InfoOutlined fontSize="small" sx={{ color: "#6b7280" }} />,
};

const PredictionSummary: React.FC<Props> = ({
    prediction,
    canMutate,
    applying,
    onApply,
}) => {
    const risk = prediction.customerRiskLevel;

    return (
        <CollectSectionCard>
            <CollectSectionHeader>
                <Box>
                    <CollectSectionTitle>
                        {prediction.invoiceDocNumber
                            ? `Invoice ${prediction.invoiceDocNumber}`
                            : prediction.customerDisplayName || "Customer"}{" "}
                        receivables prediction
                    </CollectSectionTitle>
                    <CollectSectionSubtitle>
                        {prediction.predictionType === "invoice" &&
                            prediction.invoiceDocNumber
                            ? `Invoice ${prediction.invoiceDocNumber} · `
                            : ""}
                        Generated {fmtDate(prediction.generatedAt)} · model{" "}
                        {prediction.modelVersion}
                    </CollectSectionSubtitle>
                </Box>
                <Stack direction="row" spacing={1} alignItems="center">
                    <Chip
                        label={PREDICTION_RISK_LEVEL_LABELS[risk]}
                        sx={{
                            fontFamily: FONT,
                            fontWeight: 700,
                            backgroundColor: RISK_COLORS[risk] + "1a",
                            color: RISK_COLORS[risk],
                        }}
                    />
                    <Chip
                        label={PREDICTION_CONFIDENCE_LABELS[prediction.confidenceLevel]}
                        variant="outlined"
                        sx={{ fontFamily: FONT, fontWeight: 600 }}
                    />
                </Stack>
            </CollectSectionHeader>

            {/* Headline KPIs */}
            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: {
                        xs: "repeat(2, 1fr)",
                        md: "repeat(4, 1fr)",
                    },
                    gap: 1.5,
                }}
            >
                <CollectKpiCard accent={colors.primary}>
                    <CollectKpiLabel>Reliability score</CollectKpiLabel>
                    <CollectKpiValue>{prediction.reliabilityScore}</CollectKpiValue>
                    <CollectKpiHint>out of 100</CollectKpiHint>
                </CollectKpiCard>
                <CollectKpiCard accent="#16a34a">
                    <CollectKpiLabel>Pays on time</CollectKpiLabel>
                    <CollectKpiValue>
                        {pct(prediction.onTimeProbability)}
                    </CollectKpiValue>
                    <CollectKpiHint>estimated likelihood</CollectKpiHint>
                </CollectKpiCard>
                <CollectKpiCard accent="#f59e0b">
                    <CollectKpiLabel>Pays late</CollectKpiLabel>
                    <CollectKpiValue>{pct(prediction.lateProbability)}</CollectKpiValue>
                    <CollectKpiHint>
                        ~{Math.max(0, Math.round(prediction.expectedDaysLate))} days late
                    </CollectKpiHint>
                </CollectKpiCard>
                <CollectKpiCard accent="#dc2626">
                    <CollectKpiLabel>Non-payment risk</CollectKpiLabel>
                    <CollectKpiValue>
                        {pct(prediction.nonPaymentRiskProbability)}
                    </CollectKpiValue>
                    <CollectKpiHint>estimated risk</CollectKpiHint>
                </CollectKpiCard>
            </Box>

            {/* Summary copy */}
            {prediction.summary && (
                <Typography
                    sx={{
                        fontFamily: FONT,
                        fontSize: "0.95rem",
                        color: "#1f2937",
                        mt: 2.5,
                        lineHeight: 1.6,
                    }}
                >
                    {prediction.summary}
                </Typography>
            )}
            {prediction.confidenceExplanation && (
                <Typography
                    sx={{
                        fontFamily: FONT,
                        fontSize: "0.85rem",
                        color: "#6b7280",
                        mt: 1,
                    }}
                >
                    {prediction.confidenceExplanation}
                </Typography>
            )}

            {/* Reason codes */}
            {prediction.reasonCodes?.length > 0 && (
                <Box sx={{ mt: 2.5 }}>
                    <Typography
                        sx={{
                            fontFamily: FONT,
                            fontSize: "0.8rem",
                            fontWeight: 700,
                            color: colors.primary,
                            textTransform: "uppercase",
                            letterSpacing: "0.08em",
                            mb: 1,
                        }}
                    >
                        Why this prediction
                    </Typography>
                    <Stack spacing={1}>
                        {prediction.reasonCodes.map((r, idx) => (
                            <Box
                                key={`${r.code}-${idx}`}
                                sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}
                            >
                                {REASON_ICON[r.direction]}
                                <Typography
                                    sx={{
                                        fontFamily: FONT,
                                        fontSize: "0.875rem",
                                        color: "#374151",
                                    }}
                                >
                                    {r.explanation}
                                </Typography>
                            </Box>
                        ))}
                    </Stack>
                </Box>
            )}

            {/* Recommended action */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 2,
                    mt: 2.5,
                    p: "16px 18px",
                    borderRadius: "12px",
                    backgroundColor: "rgba(30, 193, 149, 0.08)",
                    border: "1px solid rgba(30, 193, 149, 0.25)",
                    flexWrap: "wrap",
                }}
            >
                <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
                    <TrendingFlat sx={{ color: colors.mainSecondary }} />
                    <Box>
                        <Typography
                            sx={{
                                fontFamily: FONT,
                                fontSize: "0.72rem",
                                fontWeight: 700,
                                color: "#0f7a5b",
                                textTransform: "uppercase",
                                letterSpacing: "0.08em",
                            }}
                        >
                            Recommended next step
                        </Typography>
                        <Typography
                            sx={{
                                fontFamily: FONT,
                                fontSize: "0.95rem",
                                fontWeight: 600,
                                color: "#0f7a5b",
                            }}
                        >
                            {RECOVERY_ACTION_LABELS[prediction.recommendedRecoveryAction]}
                        </Typography>
                    </Box>
                </Box>
                {canMutate && (
                    <CollectPrimaryButton
                        startIcon={
                            applying ? (
                                <CircularProgress size={16} color="inherit" />
                            ) : (
                                <CheckCircleOutline />
                            )
                        }
                        onClick={onApply}
                        disabled={applying}
                    >
                        {applying ? "Applying…" : "Apply & create task"}
                    </CollectPrimaryButton>
                )}
            </Box>

            {/* Disclaimer — always shown */}
            <CollectInfoBanner tone="info" style={{ marginTop: 20, marginBottom: 0 }}>
                <InfoOutlined fontSize="small" />
                <span>{prediction.disclaimerText}</span>
            </CollectInfoBanner>
        </CollectSectionCard>
    );
};

export default PredictionSummary;
